// src/pages/Auth.jsx
"use client";
import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useAuth } from "./context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { API_BASE_URL } from "../config/api.config";
import logoBlack from "../images/logoblack.svg";

const API = API_BASE_URL;

// Optional: create a configured axios instance
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export default function Auth() {
  const { login } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0); // seconds
  const navigate = useNavigate();

  // countdown for resend button
  useEffect(() => {
    if (!resendIn) return;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const canSubmitEmail = useMemo(() => {
    // very light email check
    return /\S+@\S+\.\S+/.test(email);
  }, [email]);

  // Auth.jsx (handlers)
  const normalizeEmail = (e) => e.trim().toLowerCase();

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const emailNorm = normalizeEmail(email);
    setLoading(true);
    try {
      await api.post("/auth/client/request-otp", { email: emailNorm });
      setEmail(emailNorm); // keep normalized email in state
      setStep(2);
      setResendIn(60);
      toast({
        title: "📱 Code sent",
        description: `We sent a code to ${emailNorm}`,
      });
    } catch (err) {
      toast({
        title: "Couldn’t send code",
        description: err?.response?.data?.message ?? "Try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const emailNorm = normalizeEmail(email);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/client/verify-otp", {
        email: emailNorm,
        code: otp, // use the exact key the backend expects
      });
      const token = data?.token ?? data?.accessToken;
      const serverUser = data?.user;
      if (!serverUser || !token) throw new Error("Missing user or token");

      localStorage.setItem("bookatherapy_token", token);
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      login(serverUser, token);
      toast({ title: "✅ Logged in" });

      // Check for redirect after login
      const redirectPath = localStorage.getItem("redirectAfterLogin");
      if (redirectPath) {
        localStorage.removeItem("redirectAfterLogin");
        navigate(redirectPath, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      toast({
        title: "Verification failed",
        description: err?.response?.data?.message ?? "Request a new code.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || !email) return;
    setLoading(true);
    try {
      await api.post(`/auth/client/request-otp`, { email });
      setResendIn(60);
      toast({
        title: "Code re-sent",
        description: `Check your inbox for a new code.`,
      });
    } catch (err) {
      toast({
        title: "Couldn’t resend",
        description: err?.response?.data?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen luxury-gradient flex items-center justify-center p-3 sm:p-4">
      <div className="max-w-md w-full luxury-card p-5 sm:p-8 rounded-2xl sm:rounded-3xl">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mx-auto mb-6">
            <img
              src={logoBlack}
              alt="BookaTherapy Logo"
              className="h-16 w-auto"
            />
          </div>
          <p className="text-gray-600 mb-4">
            AI-powered therapy matching platform
          </p>
          <div className="bg-navy-50 border border-navy-200 rounded-xl p-4">
            <p className="text-sm text-navy-800 font-medium">
              Enter your email, we'll send you a one-time code to sign in.
            </p>
          </div>
        </div>

        {step === 1 ? (
          <form onSubmit={handleEmailSubmit} className="space-y-6">
            <div>
              <label className="block text-base font-medium text-gray-700 mb-3">
                Email Address
              </label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="luxury-input text-lg py-4"
                placeholder="your@email.com"
              />
            </div>

            <Button
              type="submit"
              className="luxury-button w-full py-4 text-lg"
              disabled={loading || !canSubmitEmail}>
              {loading ? "Sending..." : "Continue with Email"}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                Check your email
              </h2>
              <p className="text-gray-600">
                We sent a 6-digit code to <strong>{email}</strong>
              </p>
            </div>

            <div>
              <label className="block text-base font-medium text-gray-700 mb-3">
                Verification Code
              </label>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                className="luxury-input text-center text-3xl tracking-widest"
                placeholder="••••••"
              />
            </div>

            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep(1)}
                className="text-gray-600"
                disabled={loading}>
                ← Back
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleResend}
                disabled={loading || resendIn > 0}>
                {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
              </Button>
            </div>

            <Button
              type="submit"
              disabled={otp.length !== 6 || loading}
              className="luxury-button w-full py-4 text-lg disabled:opacity-50">
              {loading ? "Verifying..." : "Complete Login"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
